import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@fiverr-private/button';
import { Typography } from '@fiverr-private/typography';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Control, FormElement, FormHelperText } from '@fiverr-private/forms';
import pathfinder from '@fiverr-private/pathfinder';
import { translate } from '../../utils/translate';
import { getEventName, getMixpanelEventName, getGrafanaEventName } from '../../utils/eventHelpers';
import { ACTIVATION_CODE_LENGTH, ActivationErrorType, RESEND_SUCCESS_DURATION_MS } from '../../constants';
import { globalBigQueryReporter, globalMixpanelReporter, globalGrafanaReporter } from '../../events';
import { CodeInput } from '../shared/CodeInput';
import { IdentificationFormHeader } from '../shared/IdentificationFormHeader';
import { IdentificationFormFooter } from '../shared/IdentificationFormFooter';
import { IdentificationOnboardingFormFooter } from '../shared/IdentificationOnboardingFormFooter';
import { Variant } from '../../types';
import { useActivateWithCode } from './hooks/useActivateWithCode';
import Resend from './Resend';
import * as classes from './style.ve.css';

interface CodeActivationStepProps {
    email: string;
    variant?: Variant;
    isSkippable?: boolean;
    postActivationDelay?: number;
    onGoToChangeEmail?: () => void;
    onSkip?: () => void;
    onError?: (error: ActivationErrorType) => void;
    onSuccess?: () => void;
    initialFocusRef?: React.RefObject<HTMLInputElement>;
    mixpanelEnrichment?: Partial<Record<string, string | number | boolean>>;
    bigQueryEnrichment?: Partial<Record<string, string | number | boolean>>;
}

export const CodeActivationStep = ({
    email,
    variant = 'default',
    isSkippable = true,
    postActivationDelay = 0,
    onSkip,
    onSuccess,
    onError,
    onGoToChangeEmail,
    initialFocusRef,
    mixpanelEnrichment = {},
    bigQueryEnrichment = {},
}: CodeActivationStepProps) => {
    const isShown = useRef(false);
    const [isCheckingCode, setIsCheckingCode] = useState(false);
    const [showResendSuccess, setShowResendSuccess] = useState(false);
    const [activationError, setActivationError] = useState<ActivationErrorType | null>(null);
    const [activationCode, setActivationCode] = useState('');

    useEffect(() => {
        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bigQueryEnrichment);
        if (!isShown.current) {
            globalMixpanelReporter.send(getMixpanelEventName(variant, 'show'));
            globalBigQueryReporter.send(getEventName(variant, 'show'));
            globalGrafanaReporter.count(getGrafanaEventName(variant, 'show'));
            isShown.current = true;
        }
    }, [mixpanelEnrichment, bigQueryEnrichment, variant]);

    useEffect(() => {
        if (!showResendSuccess) {
            return;
        }

        if (isCheckingCode) {
            setShowResendSuccess(false);
        }

        setTimeout(() => setShowResendSuccess(false), RESEND_SUCCESS_DURATION_MS);
    }, [showResendSuccess, isCheckingCode, setShowResendSuccess]);

    const activate = useActivateWithCode({
        postActivationDelay,
        onSuccess,
        onError,
        setError: setActivationError,
        setIsCheckingCode,
        variant,
    });

    const handleSkip = () => {
        globalMixpanelReporter.send(getMixpanelEventName(variant, 'skip'));
        globalBigQueryReporter.send(getEventName(variant, 'skip'));
        globalGrafanaReporter.count(getGrafanaEventName(variant, 'skip'));
        onSkip?.();
    };

    const handleGoToHomepage = () => {
        globalMixpanelReporter.send(getMixpanelEventName(variant, 'homepage_cta'));
        globalBigQueryReporter.send(getEventName(variant, 'homepage_cta'));
        globalGrafanaReporter.count(getGrafanaEventName(variant, 'homepage_cta'));
    };

    const { title, subtitle } = useMemo(
        () => ({
            title: {
                go: translate('activation.title'),
                default: translate('activation.title'),
                purchase: translate('activation.purchase_title'),
                team: translate('activation.team_title'),
                onboarding: translate('activation.title'),
            } satisfies Record<Variant, string>,
            subtitle: {
                go: translate('activation.subtitle'),
                default: translate('activation.subtitle'),
                purchase: translate('activation.purchase_subtitle'),
                team: translate('activation.team_subtitle'),
                onboarding: translate('activation.subtitle'),
            } satisfies Record<Variant, string>,
        }),
        []
    );

    const showChangeEmail = variant !== 'team';
    const isOnboarding = variant === 'onboarding';

    const skipButton = isSkippable && onSkip && (
        <Button onClick={handleSkip} variant="ghost" className={classes.skipButton}>
            {translate(`activation.remind_me_later`)}
        </Button>
    );

    const submitButton = (
        <Button
            onClick={() => activate(activationCode)}
            loading={isCheckingCode}
            disabled={activationCode.length !== ACTIVATION_CODE_LENGTH}
            marginLeft="auto"
            fullWidth={variant === 'team'}
        >
            {variant === 'team' ? translate('activation.team_submit') : translate('general.submit')}
        </Button>
    );

    return (
        <Container display="flex" direction="column" height="100%" minHeight={0}>
            <Container minHeight={0} overflowY="auto" maxWidth={isOnboarding ? '550px' : undefined}>
                <IdentificationFormHeader
                    title={title[variant]}
                    subTitle={
                        <>
                            {subtitle[variant]}
                            <Stack alignItems="baseline" gap="2" wrap="wrap">
                                <Typography color="grey_1100" fontWeight="semibold">
                                    {email}.
                                </Typography>
                                {showChangeEmail && (
                                    <Typography
                                        size="b_sm"
                                        cursor="pointer"
                                        color="grey_1000"
                                        fontWeight="semibold"
                                        onClick={onGoToChangeEmail}
                                        whiteSpace="nowrap"
                                    >
                                        (
                                        <Typography as="span" decoration="underline">
                                            {translate('activation.change_email_cta')}
                                        </Typography>
                                        )
                                    </Typography>
                                )}
                            </Stack>
                        </>
                    }
                />

                <Stack direction="column" gap="4" paddingTop="14" minHeight={0} overflowY="auto" padding="px">
                    <Control isError={!!activationError}>
                        <FormElement>
                            <CodeInput
                                autoFocus
                                codeLength={ACTIVATION_CODE_LENGTH}
                                isError={!!activationError}
                                onChange={(value) => setActivationCode(value)}
                                onSubmit={(value) => activate(value)}
                                firstInputRef={initialFocusRef}
                            />
                        </FormElement>
                        {activationError && <FormHelperText error={translate(`errors.general.${activationError}`)} />}
                    </Control>
                    <Resend
                        email={email}
                        setShowResendSuccess={setShowResendSuccess}
                        showResendSuccess={showResendSuccess}
                    />
                </Stack>
            </Container>

            {isOnboarding ? (
                <IdentificationOnboardingFormFooter>
                    <Container marginLeft={{ default: '0', md: 'auto' }}>{skipButton}</Container>
                    <Container marginLeft={{ default: 'auto', md: '0' }}>{submitButton}</Container>
                </IdentificationOnboardingFormFooter>
            ) : (
                <IdentificationFormFooter>
                    {skipButton}
                    {!isSkippable && (
                        <Button
                            href={pathfinder('homepage', {}, { query: { source: 'identification_activation_modal' } })}
                            onClick={handleGoToHomepage}
                            variant="ghost"
                            className={classes.skipButton}
                        >
                            {translate(`activation.back_to_homepage`)}
                        </Button>
                    )}
                    {submitButton}
                </IdentificationFormFooter>
            )}
        </Container>
    );
};

export default CodeActivationStep;
